import { useSetRebuyItems } from "@babackapp/return-widget-components/hooks/use-set-rebuy-items";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import type {
  CartItem,
  EmbedToWidgetMessage,
} from "@babackapp/return-widget-components/types/shop-now";
import { publicGetVariantPricing } from "@babackapp/private-api-client/client/public-get-variant-pricing";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadCart, saveCart } from "../lib/shop-now-cart";
import { postToParent } from "../lib/shop-now-embed";
import { useWidgetParams } from "./use-widget-params";

type UseShopNowResolveResult = {
  items: CartItem[];
  currency: string;
  cartTotal: number;
  /** 1 + tax rate for HT shops. Apply to item.price * item.quantity to get TTC sub-total. */
  taxMultiplier: number;
  handleAddMore: () => void;
  handleConfirm: () => void;
};

/**
 * Overlay current catalogue prices onto cart items fetched from /cart.js.
 *
 * BBS-193: /cart.js prices may be stale if the customer loaded the rebuy banner
 * before a price change.  The catalogue (catalogProductVariantPricing) is the
 * same source used by the backend when creating the Stripe intent, so syncing
 * the display to it prevents displayed amount ≠ charged amount.
 *
 * Variants absent from the catalogue keep their original /cart.js price
 * (same safe-default as the backend).
 */
async function refreshPrices(
  shopId: string,
  items: CartItem[],
): Promise<CartItem[]> {
  try {
    const variantIds = items.map((i) => String(i.variantId));
    const pricings = await publicGetVariantPricing(shopId, variantIds);
    const priceMap = new Map(pricings.map((p) => [p.variantId, p.price]));
    return items.map((item) => {
      const freshPrice = priceMap.get(String(item.variantId));
      return freshPrice != null ? { ...item, price: freshPrice } : item;
    });
  } catch {
    // Network error or catalogue unavailable — fall back to /cart.js prices.
    // The backend will still charge the correct amount; only the display may be off.
    return items;
  }
}

export function useShopNowResolve(): UseShopNowResolveResult {
  const navigate = useNavigate();
  const { orderId, shopInternalId } = useWidgetParams();
  const { data: order } = usePublicOrder(orderId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);
  const setRebuyItems = useSetRebuyItems(orderId);

  const cached = loadCart(orderId);
  const [items, setItems] = useState<CartItem[]>(cached?.items ?? []);
  const [currency, setCurrency] = useState(cached?.currency ?? "EUR");

  // Generation counter: incremented on every refresh start. A completed refresh
  // discards its result if the generation changed (i.e. a newer refresh superseded it).
  const refreshGen = useRef(0);

  useEffect(() => {
    postToParent({ type: "WIDGET_SHOP_NOW_READY" });
  }, []);

  // Refresh cached items on mount so stale sessionStorage prices are corrected
  // even when the customer navigates back to this page without re-shopping.
  useEffect(() => {
    if (!cached?.items?.length) return;
    const gen = ++refreshGen.current;
    refreshPrices(shopInternalId, cached.items).then((refreshed) => {
      if (refreshGen.current !== gen) return; // superseded by a newer refresh
      setItems(refreshed);
      saveCart(orderId, refreshed, cached.currency ?? "EUR");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount

  const applyItems = useCallback(
    async (raw: CartItem[], newCurrency: string) => {
      const gen = ++refreshGen.current;
      const refreshed = await refreshPrices(shopInternalId, raw);
      if (refreshGen.current !== gen) return; // superseded by a concurrent call
      setItems(refreshed);
      setCurrency(newCurrency);
      saveCart(orderId, refreshed, newCurrency);
    },
    [shopInternalId, orderId],
  );

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const msg = event.data as EmbedToWidgetMessage;
      if (msg?.type === "SHOP_NOW_RESUME") {
        void applyItems(msg.items, "EUR");
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [applyItems]);

  // Apply taxMultiplier so cartTotal (HT from Shopify /cart.js on HT shops) is scaled to
  // TTC — same basis as exchangeCreditTotal, which is always TTC.
  const cartTotal = items.reduce(
    (sum, item) =>
      sum + item.price * item.quantity * financialSummary.taxMultiplier,
    0,
  );

  const handleAddMore = () =>
    postToParent({ type: "SHOP_NOW_START", clearCart: false });

  const handleConfirm = () => {
    setRebuyItems(items);
    const remaining = financialSummary.exchangeCreditTotal - cartTotal;
    navigate(remaining > 0 ? "../shop-now-remaining-credit" : "../return-mode");
  };

  return {
    items,
    currency,
    cartTotal,
    taxMultiplier: financialSummary.taxMultiplier,
    handleAddMore,
    handleConfirm,
  };
}
